import { ChartTooltip } from "@similarweb/ui-components";

import { type Translate, useTranslation } from "pro-features/components/WithTranslation";
import { NOT_AVAILABLE } from "app/filters/const";

import { type ChartTooltipColumnDefinition, ChartTooltipWrapper } from "../../components/ChartTooltipWrapper";

import type { ChartSeriesItemData } from "./types";
import { changeValueFormatter, shareValueFormatter, volumeFormatter } from "./utils";

interface ChartTooltipData extends ChartSeriesItemData {
    metric: string;
}

function getColumnDefinition(t: Translate) {
    return [
        {
            key: "keywordList",
            title: t("demand.analysis.search.trends.map.table.comparison.volume.title"),
            selector: "keywordList",
            align: "left",
            size: "146px",
            borderRight: true,
            render: ({ keywordList }) => (
                <div data-automation="chart-tooltip-keyword-list-name">
                    <ChartTooltip.LabelCell
                        label={keywordList?.name ?? ""}
                        backgroundColor={keywordList?.color}
                    />
                </div>
            ),
        },
        {
            key: "volume",
            title: t("demand.analysis.search.trends.map.tooltip.metric.name"),
            selector: "volume",
            align: "right",
            size: "50px",
            render: ({ volume }) => (
                <div data-automation="chart-tooltip-volume-value">
                    <ChartTooltip.ValueCell value={volumeFormatter(volume)} />
                </div>
            ),
        },
        {
            key: "share",
            title: "",
            selector: "share",
            align: "right",
            size: "50px",
            borderRight: true,
            render: ({ share }) => (
                <div data-automation="chart-tooltip-share-value">
                    <ChartTooltip.ValueCell value={shareValueFormatter(share)} />
                </div>
            ),
        },
        {
            key: "change",
            title: t("common.tooltip.change"),
            selector: "change",
            align: "right",
            size: "65px",
            render: ({ change }) => (
                <div data-automation="chart-tooltip-change-value">
                    {change !== 0 ? (
                        <ChartTooltip.ChangeCell
                            value={changeValueFormatter(change)}
                            isDecrease={change < 0}
                        />
                    ) : (
                        <ChartTooltip.ValueCell value={NOT_AVAILABLE} />
                    )}
                </div>
            ),
        },
    ] satisfies ChartTooltipColumnDefinition<ChartTooltipData>[];
}

interface ComparisonTooltipProps {
    title: string;
    data: ChartSeriesItemData[];
}

export const TrafficByCountryMapTooltipComparison = ({ title, data }: ComparisonTooltipProps) => {
    const t = useTranslation();
    const columnDefinition = getColumnDefinition(t);
    const metricName = t("demand.analysis.search.trends.map.table.comparison.volume.title");

    return (
        <ChartTooltipWrapper<ChartTooltipData>
            data-automation="map-tooltip"
            title={title}
            data={data.map((item) => ({ ...item, metric: metricName }))}
            columnDefinition={columnDefinition}
        />
    );
};
